import React, { useState } from 'react';
import {
  DollarSign, TrendingUp, CreditCard, CheckCircle, Building2,
  Calculator, Shield, Calendar, AlertTriangle, CircleHelp,
} from 'lucide-react';
import { InfoBadge } from '../UI/InfoBadge';
import LeaveTypeBadge from '../UI/LeaveTypeBadge';
import AccordionSection from '../UI/AccordionSection';

/**
 * Shared employee payroll breakdown display used in both Regular Payroll (Step 3
 * detail panel) and Final Settlements (Step 2/3 review). Accepts an enriched
 * EmployeePayroll data object (from the detail serializer) and renders summary
 * cards, earnings/deductions columns, reimbursement lines under gross pay
 * when money was paid back outside wages, and collapsible supplementary sections.
 *
 * Props:
 *   payroll       – enriched EmployeePayroll object
 *   formatCurrency – currency formatter fn
 *   compact       – (optional) render in a narrower / card-embedded context
 */
const EmployeePayrollBreakdown = ({ payroll, formatCurrency, compact = false }) => {
  const [accordions, setAccordions] = useState({
    rates: false,
    deductions: false,
    ytd: false,
    awCeiling: false,
  });

  if (!payroll) return null;

  const isCpfApplicable = ['Singapore Citizen', 'Permanent Resident'].includes(
    payroll.employee_employment_pass_type
  );
  const fwlBreakdown = payroll.foreign_worker_levy_breakdown || {};

  const formatYearMonth = (value) => {
    const match = /^(\d{4})-(\d{2})$/.exec(value || '');
    if (!match) return value || '';
    return new Date(Number(match[1]), Number(match[2]) - 1, 1).toLocaleDateString(
      'en-SG',
      { month: 'short', year: 'numeric' }
    );
  };

  const fwlBillPeriod = formatYearMonth(fwlBreakdown.bill_period);
  const fwlPayrollPeriod = formatYearMonth(fwlBreakdown.payroll_period);
  const fwlSourceLabel = fwlBreakdown.levy_source === 'mom_bill_actual'
    ? `${fwlBillPeriod} bill`
    : fwlBreakdown.levy_source === 'latest_mom_bill_monthly_rate'
      ? `${fwlBillPeriod} rate`
      : 'Estimate';

  const formatCompactNumber = (value, maxDecimals = 1) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return value;
    if (Number.isInteger(num)) return String(num);
    return num.toFixed(maxDecimals).replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
  };

  const formatDayCount = (value) => `${formatCompactNumber(value, 1)}d`;

  const formatBackpayDate = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
    if (!match) return '';
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
      .toLocaleDateString('en-SG', { day: 'numeric', month: 'short' });
  };

  const backpayPresentation = (item) => {
    const dateLabel = formatBackpayDate(item.target_date);
    const entitlement = item.entitlement_type || '';
    let kind = 'Backpay';
    if (entitlement.includes('overtime')) kind = 'OT';
    else if (entitlement.includes('recovery')) kind = 'Recovery';

    let quantity = '';
    if (kind === 'OT') {
      const hours = parseFloat(item.ot_hours || item.hours_worked);
      if (hours > 0) quantity = `${formatCompactNumber(hours, 2)}h`;
    }

    return {
      label: dateLabel ? `${dateLabel} ${kind}` : (item.description || 'Backpay'),
      quantity,
      summary: item.calculation_summary || '',
      description: item.description || '',
    };
  };

  // Reimbursements are cash paid back. They sit between net pay and final
  // payment: added to what the bank pays, left out of gross, CPF, and salary cost.
  const reimbursementLines = (() => {
    const fromAdjustment = ((payroll.adjustment_line_items || {}).reimbursements || [])
      .map((item) => ({
        label: (item.description || '').trim() || 'Reimbursement',
        amount: parseFloat(item.amount) || 0,
      }))
      .filter((item) => item.amount > 0);
    if (fromAdjustment.length > 0) return fromAdjustment;

    const breakdown = payroll.reimbursement_breakdown;
    if (!breakdown || typeof breakdown !== 'object' || Array.isArray(breakdown)) return [];
    return Object.entries(breakdown)
      .map(([label, amount]) => ({ label, amount: parseFloat(amount) || 0 }))
      .filter((item) => item.amount > 0);
  })();
  const statedReimbursement = parseFloat(payroll.total_reimbursements) || 0;
  const netPayAmount = parseFloat(payroll.net_pay) || 0;
  const finalPayAmount = parseFloat(payroll.final_payment) || 0;
  const encashmentAmount = parseFloat(payroll.leave_encashment_amount) || 0;
  const impliedReimbursement = Math.round((finalPayAmount - netPayAmount - encashmentAmount) * 100) / 100;
  const reimbursementTotal = statedReimbursement > 0
    ? statedReimbursement
    : (impliedReimbursement > 0.009 ? impliedReimbursement : 0);
  const listedReimbursementTotal = reimbursementLines.reduce((sum, item) => sum + item.amount, 0);
  const unlistedReimbursement = reimbursementTotal - listedReimbursementTotal;
  const reimbursementDisplayLines = unlistedReimbursement > 0.009
    ? [...reimbursementLines, { label: 'Reimbursement', amount: unlistedReimbursement }]
    : reimbursementLines;
  const showReimbursements = reimbursementTotal > 0 || reimbursementDisplayLines.length > 0;

  // ── Summary Cards ──────────────────────────────────────────────────────────
  // Five cards, one row. A reimbursement is a line under gross pay, not a
  // sixth total: the bank figure is already Final Payment.
  const SummaryCards = () => {
    const cards = [
      { icon: DollarSign, iconBg: 'bg-gray-100', iconColor: 'text-gray-600', value: payroll.gross_pay, label: 'Gross Pay', textColor: 'text-gray-900', labelClass: 'text-gray-500', borderClass: '' },
      { icon: TrendingUp, iconBg: 'bg-red-100', iconColor: 'text-red-600 rotate-180', value: payroll.total_deductions, label: 'Deductions', textColor: 'text-red-600', labelClass: 'text-gray-500', prefix: '-', borderClass: '' },
      { icon: CreditCard, iconBg: 'bg-blue-100', iconColor: 'text-blue-600', value: payroll.net_pay, label: 'Net Pay', textColor: 'text-gray-900', labelClass: 'text-gray-500', borderClass: '' },
      { icon: CheckCircle, iconBg: 'bg-green-100', iconColor: 'text-green-600', value: payroll.final_payment || payroll.net_pay, label: 'Final Payment', textColor: 'text-green-700', labelClass: 'text-green-600', borderClass: 'border-green-200' },
      { icon: Building2, iconBg: 'bg-red-100', iconColor: 'text-red-600', value: payroll.employer_total_cost, label: 'Cost to Co.', textColor: 'text-red-700', labelClass: 'text-red-600', borderClass: 'border-red-200' },
    ];
    const gridClass = compact ? 'grid-cols-3 sm:grid-cols-5' : 'grid-cols-5';
    return (
      <div className={`grid ${gridClass} gap-2 mb-6`}>
        {cards.map(({ icon: Icon, iconBg, iconColor, value, label, textColor, labelClass, prefix, borderClass }) => (
          <div key={label} className={`bg-white rounded-2xl shadow-sm border ${borderClass} p-3`}>
            <div className="flex items-start">
              <div className={`w-8 h-8 ${iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4 h-4 ${iconColor}`} />
              </div>
              <div className="ml-2 min-w-0">
                <p className={`text-sm font-bold ${textColor} tabular-nums truncate`}>{prefix || ''}{formatCurrency(value)}</p>
                <p className={`text-[10px] ${labelClass}`}>{label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ── Earnings Column ────────────────────────────────────────────────────────
  const isFinalSettlement = payroll.payroll_run?.run_type === 'final_settlement';
  const fsBasicEarned = parseFloat(payroll.basic_salary_earned) || 0;
  const fsBasicDetails = payroll.calculation_metadata?.basic_salary_details;
  const fsUnpaidLeaveDays = parseFloat(
    fsBasicDetails?.authorised_unpaid_leave_days ?? payroll.unpaid_leave_days
  ) || 0;
  const fsPaidDays = parseFloat(
    fsBasicDetails?.paid_working_days ?? payroll.working_days_worked
  ) || 0;
  const fsScheduledDays = parseFloat(
    fsBasicDetails?.scheduled_working_days
      ?? (fsPaidDays + fsUnpaidLeaveDays)
  ) || 0;
  const fsFullMonthDays = parseFloat(
    fsBasicDetails?.full_month_working_days
      ?? payroll.working_days_entitled
  ) || 0;
  const fsProrationDailyRate = parseFloat(
    fsBasicDetails?.proration_daily_rate
  ) || (fsPaidDays > 0 ? fsBasicEarned / fsPaidDays : 0);
  const fsShowEarned = isFinalSettlement && fsBasicEarned < parseFloat(payroll.basic_salary_monthly);

  const EarningsColumn = () => (
    <div className="bg-white rounded-2xl shadow-sm border p-4 min-w-0">
      <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
          <TrendingUp className="w-3.5 h-3.5 text-green-600" />
        </div>
        Earnings
      </h5>
      <div className="space-y-2 text-sm">
        {fsShowEarned ? (
          <div className="flex justify-between items-center">
            <div className="min-w-0">
              <span className="text-gray-600 flex items-center gap-1">
                Basic Salary
                <InfoBadge color="gray" label={formatDayCount(fsPaidDays)} />
              </span>
              <span className="mt-0.5 block text-[10px] text-gray-500 tabular-nums">
                {formatCurrency(fsProrationDailyRate)}/day × {formatDayCount(fsPaidDays)}
                {fsUnpaidLeaveDays > 0
                  ? ` · ${formatDayCount(fsScheduledDays)} scheduled − ${formatDayCount(fsUnpaidLeaveDays)} unpaid`
                  : ''}
              </span>
            </div>
            <div className="tooltip-container">
              <span className="tooltip-trigger font-medium text-gray-900 tabular-nums cursor-help">{formatCurrency(fsBasicEarned)}</span>
              <div className="tooltip-content tooltip-top-end w-80">
                <div className="tooltip-arrow"></div>
                <div className="text-xs space-y-1">
                  <div className="text-gray-400">MOM incomplete-month formula</div>
                  <div className="tabular-nums text-white">
                    {formatCurrency(payroll.basic_salary_monthly)} ÷ {formatCompactNumber(fsFullMonthDays)} monthly working days
                    {' = '}
                    {formatCurrency(fsProrationDailyRate)}/day
                  </div>
                  <div className="tabular-nums text-gray-300">
                    {formatCurrency(fsProrationDailyRate)}/day × {formatCompactNumber(fsPaidDays)} paid days = {formatCurrency(fsBasicEarned)}
                  </div>
                  {fsUnpaidLeaveDays > 0 && (
                    <div className="text-gray-400">
                      {formatCompactNumber(fsScheduledDays)} scheduled − {formatCompactNumber(fsUnpaidLeaveDays)} unpaid leave = {formatCompactNumber(fsPaidDays)} paid days. Unpaid leave is not deducted again.
                    </div>
                  )}
                  {parseFloat(payroll.daily_rate) > 0 && (
                    <div className="border-t border-gray-600 pt-1 mt-1 text-gray-500">
                      {formatCurrency(payroll.daily_rate)}/day annual basic rate is for OT / rest day — not incomplete-month basic salary.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Basic Salary</span>
            <span className="font-medium text-gray-900 tabular-nums">{formatCurrency(payroll.basic_salary_monthly)}</span>
          </div>
        )}

        {parseFloat(payroll.total_allowances) > 0 && (
          payroll.allowance_breakdown && Object.keys(payroll.allowance_breakdown).length > 0 ? (
            Object.entries(payroll.allowance_breakdown).map(([name, amount]) => (
              <div key={name} className="flex justify-between">
                <span className="text-gray-600">{name}</span>
                <span className="font-medium text-gray-900 tabular-nums">{formatCurrency(amount)}</span>
              </div>
            ))
          ) : (
            <div className="flex justify-between">
              <span className="text-gray-600">Allowances</span>
              <span className="font-medium text-gray-900 tabular-nums">{formatCurrency(payroll.total_allowances)}</span>
            </div>
          )
        )}
        {((payroll.adjustment_line_items || {}).allowances || [])
          .filter(i => parseFloat(i.amount) > 0)
          .map((item, idx) => (
            <div key={`allow-adj-${idx}`} className="flex justify-between text-green-600">
              <span className="flex items-center gap-1">
                {item.description || 'Allowance Adj'}
                <InfoBadge color="amber" label="Adj" />
              </span>
              <span className="font-medium tabular-nums">+{formatCurrency(item.amount)}</span>
            </div>
          ))
        }

        {parseFloat(payroll.overtime_pay) > 0 && (() => {
          const otDetails = payroll.calculation_metadata?.overtime_details;
          const ratesFixed = payroll.calculation_metadata?.rates_reference?.fixed_rates
            || payroll.mom_rates_reference?.fixed_rates;
          const weekdayHours = parseFloat(otDetails?.weekday_overtime_hours) || 0;
          const weekdayPay = parseFloat(otDetails?.weekday_overtime_pay) || 0;
          const restDayHours = parseFloat(otDetails?.rest_day_overtime_hours) || 0;
          const restDayPay = parseFloat(otDetails?.rest_day_overtime_pay) || 0;
          const hasSplit = otDetails && (weekdayHours > 0 || restDayHours > 0);
          const otRateVal = parseFloat(ratesFixed?.hourly_ot_rate) || parseFloat(payroll.overtime_hourly_rate) || 0;
          const restDayOtRateVal = parseFloat(ratesFixed?.rest_day_ot_hourly_rate)
            || parseFloat(otDetails?.rest_day_overtime_hourly_rate) || otRateVal;
          const multiplier = parseFloat(payroll.overtime_rate_multiplier) || 1.5;

          if (hasSplit) {
            return (
              <>
                {weekdayPay > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 flex items-center gap-1">Weekday OT <InfoBadge color="gray" label={`${weekdayHours}h`} /></span>
                    <div className="tooltip-container">
                      <span className="tooltip-trigger font-medium text-gray-900 tabular-nums cursor-help">{formatCurrency(weekdayPay)}</span>
                      <div className="tooltip-content tooltip-top-end w-52">
                        <div className="tooltip-arrow"></div>
                        <div className="text-xs">
                          <div className="tabular-nums text-white">{weekdayHours}h × {formatCurrency(otRateVal)}/hr = {formatCurrency(weekdayPay)}</div>
                          <div className="text-[10px] text-gray-400 mt-0.5">{multiplier}× multiplier</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {restDayPay > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 flex items-center gap-1">Rest Day OT <InfoBadge color="gray" label={`${restDayHours}h`} /></span>
                    <div className="tooltip-container">
                      <span className="tooltip-trigger font-medium text-gray-900 tabular-nums cursor-help">{formatCurrency(restDayPay)}</span>
                      <div className="tooltip-content tooltip-top-end w-52">
                        <div className="tooltip-arrow"></div>
                        <div className="text-xs">
                          <div className="tabular-nums text-white">{restDayHours}h × {formatCurrency(restDayOtRateVal)}/hr = {formatCurrency(restDayPay)}</div>
                          <div className="text-[10px] text-gray-400 mt-0.5">{multiplier}× multiplier (rest day rate)</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          }

          const otHours = parseFloat(payroll.overtime_hours) || 0;
          const otPay = parseFloat(payroll.overtime_pay) || 0;
          const fallbackRate = otRateVal || (otHours > 0 ? otPay / otHours : 0);
          return (
            <div className="flex justify-between items-center">
              <span className="text-gray-600 flex items-center gap-1">Overtime <InfoBadge color="orange" label="MOM" /> <InfoBadge color="gray" label={`${otHours}h`} /></span>
              <div className="tooltip-container">
                <span className="tooltip-trigger font-medium text-gray-900 tabular-nums cursor-help">{formatCurrency(otPay)}</span>
                <div className="tooltip-content tooltip-top-end w-52">
                  <div className="tooltip-arrow"></div>
                  <div className="text-xs">
                    <div className="tabular-nums text-white">{otHours}h × {formatCurrency(fallbackRate)}/hr = {formatCurrency(otPay)}</div>
                    <div className="text-[10px] text-gray-400 mt-0.5">OT rate inclusive of {multiplier}× multiplier</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
        {((payroll.adjustment_line_items || {}).overtime || [])
          .filter(i => parseFloat(i.amount) > 0)
          .map((item, idx) => (
            <div key={`ot-adj-${idx}`} className="flex justify-between text-green-600">
              <span className="flex items-center gap-1">
                {item.description || 'OT Adj'}
                <InfoBadge color="amber" label="Adj" />
              </span>
              <span className="font-medium tabular-nums">+{formatCurrency(item.amount)}</span>
            </div>
          ))
        }

        {parseFloat(payroll.public_holiday_pay) > 0 && (() => {
          const phDetails = payroll.calculation_metadata?.public_holiday_details;
          const phBreakdown = Array.isArray(phDetails?.breakdown) ? phDetails.breakdown : [];
          const phAmt = parseFloat(payroll.public_holiday_pay) || 0;
          const totalPhHours = parseFloat(phDetails?.total_hours_worked_on_ph) || 0;
          // If we have per-PH-day rows, render each as its own line so HR
          // sees one row per PH date with the correct rate basis.
          if (phBreakdown.length > 0) {
            return phBreakdown.map((row, idx) => {
              const rowAmt = parseFloat(row.pay_amount) || 0;
              if (rowAmt <= 0) return null;
              const rowHours = parseFloat(row.hours_worked) || 0;
              const isWorkingDay = !!row.is_working_day_ph;
              // For working-day PH, the service stores ph_daily_rate as the
              // 1.5x rest-day audit rate, while the actual payable rate is
              // the 1.0x basic daily lump sum (equal to pay_amount for 1d).
              const payableDailyRate = rowAmt;
              const hourlyRate = parseFloat(row.ph_hourly_rate) || 0;
              const dateStr = row.date
                ? new Date(row.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                : '';
              const holidayName = row.holiday_name || 'Public Holiday';
              // Unit-aware quantity badge:
              //   - Working-day PH (lump sum): "1d" — pay reconciles to
              //     1.0× basic_daily_rate regardless of hours worked
              //     (MOM Sec 88(1) does NOT pro-rate the lump sum).
              //   - Rest-day PH (hourly): "Xh" — pay reconciles to
              //     hours_worked × 1.5× basic_hourly.
              // S88 legal anchor moves into the tooltip body so HR sees
              // the unit at-a-glance and auditors still get the rule.
              const quantityLabel = isWorkingDay
                ? '1d'
                : `${rowHours % 1 === 0 ? rowHours.toFixed(0) : rowHours.toFixed(2)}h`;
              return (
                <div key={`ph-${idx}`} className="flex justify-between items-center">
                  <span className="text-gray-600 flex items-center gap-1">
                    PH Pay
                    <InfoBadge color="gray" label={quantityLabel} />
                  </span>
                  <div className="tooltip-container">
                    <span className="tooltip-trigger font-medium text-gray-900 tabular-nums cursor-help">{formatCurrency(rowAmt)}</span>
                    <div className="tooltip-content tooltip-top-end w-72">
                      <div className="tooltip-arrow"></div>
                      <div className="text-xs space-y-1">
                        <div className="text-white font-medium">{holidayName} ({dateStr})</div>
                        {isWorkingDay ? (
                          <>
                            <div className="tabular-nums text-white">1d × {formatCurrency(payableDailyRate)}/day = {formatCurrency(rowAmt)}</div>
                            <div className="text-gray-400">Paid as 1 extra day at the basic daily rate.</div>
                            <div className="text-gray-400">Basis: PH fell on a scheduled working day.</div>
                            {rowHours > 0 && (
                              <div className="text-[10px] text-gray-500 mt-1">
                                Lump sum, not pro-rated by hours. {rowHours}h worked; OT excess is paid separately.
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="tabular-nums text-white">{rowHours}h × {formatCurrency(hourlyRate)}/hr = {formatCurrency(rowAmt)}</div>
                            <div className="text-gray-400">PH on a rest/non-working day → MOM Sec 88(1):</div>
                            <div className="text-gray-400">"extra day's salary at the gross rate of pay" (1.5×)</div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            });
          }
          // Fallback: single aggregate row when no per-PH metadata is
          // available (e.g. payrolls calculated before the breakdown
          // snapshot was persisted).  We only know aggregate hours, so
          // surface that as the quantity badge (cannot disambiguate
          // working-day vs rest-day PH without the breakdown).
          return (
            <div className="flex justify-between items-center">
              <span className="text-gray-600 flex items-center gap-1">
                PH Pay
                {totalPhHours > 0 && <InfoBadge color="gray" label={`${totalPhHours % 1 === 0 ? totalPhHours.toFixed(0) : totalPhHours.toFixed(2)}h`} />}
              </span>
              <div className="tooltip-container">
                <span className="tooltip-trigger font-medium text-gray-900 tabular-nums cursor-help">{formatCurrency(phAmt)}</span>
                <div className="tooltip-content tooltip-top-end w-64">
                  <div className="tooltip-arrow"></div>
                  <div className="text-xs space-y-1">
                    <div className="text-white">Public Holiday Pay (MOM Sec 88(1))</div>
                    <div className="text-gray-400">Working-day PH: 1.0× basic daily rate (lump sum)</div>
                    <div className="text-gray-400">Rest-day PH: 1.5× basic hourly × hours</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
        {parseFloat(payroll.rest_day_pay) > 0 && (() => {
          const rdpAmt = parseFloat(payroll.rest_day_pay) || 0;
          const rdpOtDetails = payroll.calculation_metadata?.overtime_details;
          const rdpRatesFixed = payroll.calculation_metadata?.rates_reference?.fixed_rates
            || payroll.mom_rates_reference?.fixed_rates;
          const rdpDetails = payroll.calculation_metadata?.rest_day_pay_details;
          const rdpBreakdown = Array.isArray(rdpDetails?.breakdown) ? rdpDetails.breakdown : [];
          const rdpWorkedDays = Array.isArray(rdpDetails?.rest_days_worked)
            ? rdpDetails.rest_days_worked
            : rdpBreakdown;
          // Total hours actually worked on rest days (not the OT-only observed
          // count) is the meaningful figure for the legacy hourly badge.
          const rdpTotalHours = parseFloat(rdpDetails?.total_hours_worked_on_rest_days)
            || parseFloat(rdpOtDetails?.rest_day_ot_hours_observed) || 0;
          // The MOM s.37 employer-request model pays a tiered LUMP SUM of the
          // basic DAILY rate (1×/2×) plus hourly OT only beyond the normal day.
          // It is NOT a flat "hours × 1.5× hourly", so we render the real tier
          // math from the persisted per-day breakdown rather than fabricate one.
          const restDayTiered = rdpRatesFixed?.rest_day_pay_model === 'mom_tiered';
          // Under the tiered model the at-a-glance unit HR cares about is the
          // number of REST DAYS worked (each paid as a lump-sum day's salary),
          // not summed hours — "16h" wrongly primes an hourly mental model.
          const rdpDayCount = rdpWorkedDays.length;
          const rdpDayCountLabel = rdpDayCount > 0
            ? `${rdpDayCount} rest ${rdpDayCount === 1 ? 'day' : 'days'} worked`
            : '';
          // Genuine OT (hours beyond the normal day, paid at 1.5× hourly) only
          // exists in the beyond-normal-day tier; surface it separately so HR
          // can distinguish rest-day lump-sum pay from real overtime.
          const rdpTotalOtHours = Math.round(
            rdpBreakdown.reduce((s, b) => s + (parseFloat(b.ot_hours) || 0), 0) * 100
          ) / 100;
          const fmtRdDay = (iso) => {
            if (!iso || typeof iso !== 'string') return '';
            const parts = iso.split('-');
            if (parts.length < 3) return '';
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const m = parseInt(parts[1], 10);
            const d = parseInt(parts[2], 10);
            return `${months[(m || 1) - 1]} ${d}`;
          };
          return (
            <div className="flex justify-between items-center">
              <span className="text-gray-600 flex items-center gap-1">
                {restDayTiered ? 'Rest Day Pay' : 'Rest Day OT'}
                {restDayTiered ? (
                  rdpDayCount > 0 ? (
                    <>
                      <InfoBadge color="gray" label={`${rdpDayCount} ${rdpDayCount === 1 ? 'day' : 'days'}`} />
                      {rdpTotalOtHours > 0 && <InfoBadge color="orange" label={`+${rdpTotalOtHours}h OT`} />}
                    </>
                  ) : (
                    <InfoBadge color="orange" label="S37" />
                  )
                ) : (
                  rdpTotalHours > 0
                    ? <InfoBadge color="gray" label={`${rdpTotalHours}h`} />
                    : <InfoBadge color="orange" label="S37" />
                )}
              </span>
              <div className="tooltip-container">
                <span className="tooltip-trigger font-medium text-gray-900 tabular-nums cursor-help">{formatCurrency(rdpAmt)}</span>
                <div className="tooltip-content tooltip-top-end w-64">
                  <div className="tooltip-arrow"></div>
                  <div className="text-xs space-y-1">
                    {rdpBreakdown.length > 0 ? (
                      <>
                        <div className="text-white">
                          {restDayTiered ? 'Rest day pay — MOM s.37 (employer-request)' : 'Rest day pay (S37)'}
                        </div>
                        {rdpDayCountLabel && (
                          <div className="text-gray-400">{rdpDayCountLabel}</div>
                        )}
                        {rdpBreakdown.map((b, i) => {
                          const dayMult = parseFloat(b.day_multiplier) || 0;
                          const dailyRate = parseFloat(b.basic_daily_rate) || 0;
                          const otHrs = parseFloat(b.ot_hours) || 0;
                          const otRate = parseFloat(b.ot_hourly_rate) || 0;
                          const dayPay = parseFloat(b.pay_amount) || 0;
                          const hrs = parseFloat(b.hours_worked) || 0;
                          const label = fmtRdDay(b.date);
                          if (b.tier) {
                            return (
                              <div key={i} className="tabular-nums text-gray-200">
                                {label && <span className="text-gray-400">{label} ({hrs}h): </span>}
                                {formatCompactNumber(dayMult)} {dayMult === 1 ? 'day' : 'days'} × {formatCurrency(dailyRate)}
                                {otHrs > 0 && <> + {otHrs}h OT × {formatCurrency(otRate)}</>}
                                {' = '}{formatCurrency(dayPay)}
                              </div>
                            );
                          }
                          const legacyRate = parseFloat(b.rest_day_hourly_rate) || 0;
                          return (
                            <div key={i} className="tabular-nums text-gray-200">
                              {label && <span className="text-gray-400">{label}: </span>}
                              {hrs}h × {formatCurrency(legacyRate)}/hr (1.5×) = {formatCurrency(dayPay)}
                            </div>
                          );
                        })}
                        {rdpBreakdown.length > 1 && (
                          <div className="tabular-nums text-white border-t border-gray-700 pt-1 mt-1">Total = {formatCurrency(rdpAmt)}</div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="text-white">
                          {restDayTiered
                            ? 'Rest day pay — MOM s.37 tiered (1×/2× basic daily + OT beyond normal day)'
                            : 'Rest day pay (S37)'}
                        </div>
                        {rdpDayCountLabel && (
                          <div className="text-gray-400">{rdpDayCountLabel}</div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
        {parseFloat(payroll.bonus_payments) > 0 && (() => {
          const adj = payroll.adjustment_line_items || {};
          const bonusItems = (adj.bonus || []).filter(i => parseFloat(i.amount) > 0);
          if (bonusItems.length > 0) {
            return bonusItems.map((item, idx) => (
              <div key={`bonus-${idx}`} className="flex justify-between text-green-600">
                <span className="flex items-center gap-1">
                  {item.description || 'Bonus'} (AW)
                  <InfoBadge color="amber" label="Adj" />
                </span>
                <span className="font-medium tabular-nums">+{formatCurrency(item.amount)}</span>
              </div>
            ));
          }
          return (
            <div className="flex justify-between text-green-600">
              <span>Bonus (AW)</span>
              <span className="font-medium tabular-nums">+{formatCurrency(payroll.bonus_payments)}</span>
            </div>
          );
        })()}
        {(() => {
          const adj = payroll.adjustment_line_items || {};
          const backpayItems = (adj.backpay || []).filter(i => parseFloat(i.amount) > 0);
          if (backpayItems.length > 0) {
            return backpayItems.map((item, idx) => {
              const { label, quantity, summary, description } = backpayPresentation(item);
              const tooltipBody = summary || description;
              const tooltipDetail = summary && description && summary !== description
                ? description
                : '';
              return (
                <div key={`bp-${idx}`} className="flex items-center justify-between gap-2 text-green-600">
                  <div className="tooltip-container min-w-0">
                    <span className="tooltip-trigger flex items-center gap-1 min-w-0 cursor-help">
                      <span className="truncate">{label}</span>
                      {quantity && (
                        <InfoBadge color="gray" label={quantity} className="flex-shrink-0" />
                      )}
                      <InfoBadge color="amber" label="Adj" className="flex-shrink-0" />
                    </span>
                    {tooltipBody && (
                      <div className="tooltip-content tooltip-top w-64">
                        <div className="tooltip-arrow"></div>
                        <div className="text-xs space-y-1">
                          <div className="text-white">{tooltipBody}</div>
                          {tooltipDetail && (
                            <div className="text-[10px] text-gray-400">{tooltipDetail}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <span className="font-medium tabular-nums flex-shrink-0">+{formatCurrency(item.amount)}</span>
                </div>
              );
            });
          }
          if (parseFloat(payroll.other_payments) > 0) {
            return (
              <div className="flex justify-between text-green-600">
                <span>Other Payments</span>
                <span className="font-medium tabular-nums">+{formatCurrency(payroll.other_payments)}</span>
              </div>
            );
          }
          return null;
        })()}
        <div className="pt-2 mt-2 border-t border-gray-200 flex justify-between font-semibold">
          <span className="text-gray-900">Total Gross Pay</span>
          <span className="text-gray-900 tabular-nums">{formatCurrency(payroll.gross_pay)}</span>
        </div>
        {parseFloat(payroll.leave_encashment_amount) > 0 && (() => {
          const encDays = parseFloat(payroll.leave_encashment_days) || 0;
          const encAmt = parseFloat(payroll.leave_encashment_amount) || 0;
          const grossDailyRate = encDays > 0 ? encAmt / encDays : 0;
          return (
            <div className="flex justify-between items-center text-green-700 pt-2 mt-2 border-t border-dashed border-gray-200">
              <span className="flex items-center gap-1 text-sm">
                AL Encashment
                {encDays > 0 && <InfoBadge color="green" label={`${encDays.toFixed(2)}d`} />}
              </span>
              <div className="tooltip-container">
                <span className="tooltip-trigger font-medium tabular-nums cursor-help text-sm">+{formatCurrency(encAmt)}</span>
                <div className="tooltip-content tooltip-top-end w-56">
                  <div className="tooltip-arrow"></div>
                  <div className="text-xs space-y-1">
                    <div className="tabular-nums text-white">{encDays.toFixed(2)}d × {formatCurrency(grossDailyRate)}/day</div>
                    <div className="text-[10px] text-gray-400">MOM Gross Daily Rate: (12 × gross) ÷ (52 × days/wk)</div>
                    <div className="text-[10px] text-gray-400 mt-0.5">Not included in Gross Pay</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
        {showReimbursements && (
          <div className="pt-2 mt-2 border-t border-dashed border-gray-200 space-y-2">
            {reimbursementDisplayLines.length > 1 && (
              <p className="text-xs font-medium text-gray-500">Reimbursements</p>
            )}
            {reimbursementDisplayLines.map((item, index) => (
              <div key={`${item.label}-${index}`} className="flex justify-between items-center gap-2">
                <span className="text-sky-700 flex items-center gap-1 min-w-0">
                  <span className="truncate">{item.label}</span>
                  <InfoBadge color="sky" label="Non-taxable" size="xs" className="flex-shrink-0" />
                </span>
                <span className="font-medium text-sky-700 tabular-nums flex-shrink-0">{formatCurrency(item.amount)}</span>
              </div>
            ))}
            {reimbursementDisplayLines.length > 1 && (
              <div className="pt-2 mt-2 border-t border-gray-200 flex justify-between gap-2 font-semibold">
                <span className="text-gray-900">Total reimbursed</span>
                <span className="text-gray-900 tabular-nums">{formatCurrency(reimbursementTotal || listedReimbursementTotal)}</span>
              </div>
            )}
            <p className="text-[10px] text-gray-500 leading-snug">
              Added to net pay to reach final payment. Left out of gross pay, CPF, and salary cost.
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // ── Deductions Column ──────────────────────────────────────────────────────
  const DeductionsColumn = () => (
    <div className="bg-white rounded-2xl shadow-sm border p-4 min-w-0">
      <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
          <TrendingUp className="w-3.5 h-3.5 text-red-600 rotate-180" />
        </div>
        Deductions
      </h5>
      <div className="space-y-2 text-sm">
        {parseFloat(payroll.cpf_employee_contribution) > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-gray-600 flex items-center gap-1">CPF Employee <InfoBadge color="blue" label={payroll.cpf_employee_rate_display !== 'N/A' ? payroll.cpf_employee_rate_display : ''} /></span>
            <span className="font-medium text-red-600 tabular-nums">-{formatCurrency(payroll.cpf_employee_contribution)}</span>
          </div>
        )}
        {parseFloat(payroll.income_tax_deduction) > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-gray-600 flex items-center gap-1">Income Tax <InfoBadge color="red" label="IRAS" /></span>
            <span className="font-medium text-red-600 tabular-nums">-{formatCurrency(payroll.income_tax_deduction)}</span>
          </div>
        )}
        {payroll.shg_contributions_display && (
          payroll.shg_contributions_display.opt_out ? (
            <div className="flex justify-between items-center">
              <span className="text-gray-400">SHG Fund</span>
              <span className="text-xs text-gray-400 italic">Opted Out</span>
            </div>
          ) : parseFloat(payroll.shg_contributions_display.total) > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">SHG Fund</span>
              <span className="font-medium text-red-600 tabular-nums">-{formatCurrency(payroll.shg_contributions_display.total)}</span>
            </div>
          )
        )}
        {parseFloat(payroll.ir21_deduction_amount) > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-gray-600 flex items-center gap-1">IR21 <InfoBadge color="red" label="IRAS" /></span>
            <span className="font-medium text-red-600 tabular-nums">-{formatCurrency(payroll.ir21_deduction_amount)}</span>
          </div>
        )}

        {/* Leave deductions */}
        {parseFloat(payroll.leave_deduction_amount) > 0 && (
          Array.isArray(payroll.leave_deduction_breakdown) && payroll.leave_deduction_breakdown.length > 0 ? (
            payroll.leave_deduction_breakdown.map((item, idx) => {
              if (item.type === 'absence_proration') {
                const descMatch = (item.description || '').match(/^(.*?)\s*\((\d+\.?\d*)\s*days?\)\s*$/i);
                const cleanDesc = descMatch ? descMatch[1].trim() : (item.description || 'Proration');
                const prorationDays = descMatch ? descMatch[2] : null;
                const dateLabels = Array.isArray(item.date_labels) ? item.date_labels : [];
                const dateGroups = Array.isArray(item.date_groups) ? item.date_groups : [];
                const fmtDay = (iso) => {
                  const d = new Date(iso);
                  return Number.isNaN(d.getTime())
                    ? iso
                    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                };

                // Grouped layout: separates a mid-month join/leave gap (pure
                // proration — the employee wasn't on payroll yet) from genuine
                // absent-without-approved-leave days, so a recent joiner never
                // reads as AWOL since the 1st.
                if (dateGroups.length > 0) {
                  return (
                    <div key={`absence-${idx}`} className="flex justify-between items-start">
                      <div className="min-w-0">
                        <span className="text-gray-600 flex items-center gap-1">
                          Days not worked
                          {prorationDays && <InfoBadge color="gray" label={`${prorationDays}d`} />}
                        </span>
                        <div className="mt-1 flex flex-col gap-1.5">
                          {dateGroups.map((g, gi) => {
                            const isAbsence = g.kind === 'unpaid_absence';
                            return (
                              <div
                                key={gi}
                                className={`pl-2 border-l-2 ${isAbsence ? 'border-amber-200' : 'border-gray-200'}`}
                              >
                                <div className="flex items-center gap-1 flex-wrap">
                                  <span className="text-[11px] font-medium text-gray-500">{g.label}</span>
                                  {g.days != null && (
                                    <InfoBadge color={isAbsence ? 'amber' : 'gray'} label={formatDayCount(g.days)} size="xs" />
                                  )}
                                  {g.amount != null && (
                                    <span className="text-[10px] text-gray-400 tabular-nums">−{formatCurrency(g.amount)}</span>
                                  )}
                                </div>
                                <div className="text-[10px] text-gray-400 flex items-center flex-wrap gap-x-1">
                                  {Array.isArray(g.spans) && g.spans.map((sp, si) => (
                                    <span key={`sp-${si}`} className="inline-flex items-center">
                                      {fmtDay(sp.start)}
                                      <span className="text-gray-400 mx-0.5">→</span>
                                      {fmtDay(sp.end)}
                                    </span>
                                  ))}
                                  {Array.isArray(g.dates) && g.dates.length > 0 && (
                                    <span>{g.dates.map(fmtDay).join(' · ')}</span>
                                  )}
                                  {g.note && !isAbsence && <span>· {g.note}</span>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <span className="font-medium text-red-600 tabular-nums flex-shrink-0 ml-2">-{formatCurrency(item.amount)}</span>
                    </div>
                  );
                }

                // Fallback: flat labels (legacy rows or unresolved groups).
                return (
                  <div key={`absence-${idx}`} className="flex justify-between items-start">
                    <div className="min-w-0">
                      <span className="text-gray-600 flex items-center gap-1">
                        {cleanDesc}
                        {prorationDays && <InfoBadge color="gray" label={`${prorationDays}d`} />}
                      </span>
                      {dateLabels.length > 0 && (
                        <div className="mt-0.5 flex flex-col">
                          {dateLabels.map((lbl, di) => (
                            <span key={di} className="text-[10px] text-gray-400">{lbl}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <span className="font-medium text-red-600 tabular-nums flex-shrink-0 ml-2">-{formatCurrency(item.amount)}</span>
                  </div>
                );
              }
              const startDate = item.leave_start_date ? new Date(item.leave_start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : null;
              const endDate = item.leave_end_date ? new Date(item.leave_end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : null;
              const isSameDay = item.leave_start_date === item.leave_end_date;
              let dateLabel = item.leave_date_label || '';
              if (!dateLabel && startDate) {
                if (isSameDay) {
                  dateLabel = startDate;
                  // Half-day suffix only when the leave is genuinely a half-day.
                  // Model defaults are start='AM', end='PM' for full-day same-day
                  // leaves, so the previous `!== 'Full Day'` check (a string the
                  // backend never sends) wrongly tagged every full day with "(AM)".
                  if (item.leave_start_session === 'PM') {
                    dateLabel += ' (PM)';
                  } else if (item.leave_end_session === 'AM') {
                    dateLabel += ' (AM)';
                  }
                } else {
                  const startSuffix = item.leave_start_session === 'PM' ? ' (PM)' : '';
                  const endSuffix = item.leave_end_session === 'AM' ? ' (AM)' : '';
                  dateLabel = `${startDate}${startSuffix}–${endDate}${endSuffix}`;
                }
              }
              // Strip any trailing parenthetical (e.g. the holiday name in
              // "Unpaid PH (Good Friday)") from the chip — the sub-line below
              // already names WHICH holiday, and the chip is a category, not a
              // freeform description. Greedy `.*` so nested parens are stripped
              // too ("Unpaid PH (Vesak Day (In Lieu))" → "Unpaid PH"); a
              // `[^)]*` group stops at the inner ")" and leaks the rest.
              const chipLabel = (item.leave_type || 'Leave').replace(/\s*\(.*\)\s*$/, '').trim();
              const sourceBase = (item.source || '').replace(/^prior_month_tail_/, '');
              const holidayName = sourceBase === 'ph_forfeiture'
                ? (item.holiday_name || (item.leave_type || '').match(/\(([^)]+)\)/)?.[1])
                : null;
              return (
                <div key={`leave-${idx}`} className="flex justify-between items-start">
                  <div className="min-w-0">
                    <LeaveTypeBadge leaveTypeName={chipLabel} />
                    <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                      {(dateLabel || holidayName) && (
                        <span className="text-[10px] text-gray-400">
                          {[dateLabel, holidayName].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      {item.working_days_in_period && <InfoBadge color="gray" label={formatDayCount(item.working_days_in_period)} />}
                      {item.source && (() => {
                        const s = item.source;
                        const isTail = s.startsWith('prior_month_tail_');
                        const base = isTail ? s.replace('prior_month_tail_', '') : s;
                        const badges = [];
                        if (isTail) badges.push({ color: 'purple', label: 'Prior Mth' });
                        if (base === 'probation_sick') badges.push({ color: 'amber', label: 'Probation' });
                        if (base === 'ph_forfeiture') badges.push({ color: 'red', label: 'PH Forfeit' });
                        return badges.map((b, i) => <InfoBadge key={i} color={b.color} label={b.label} />);
                      })()}
                    </div>
                  </div>
                  <div className="tooltip-container flex-shrink-0 ml-2">
                    <span className="tooltip-trigger font-medium text-red-600 tabular-nums cursor-help">-{formatCurrency(item.deduction_amount)}</span>
                    {Array.isArray(item.monthly_segments) && item.monthly_segments.length > 0 && (
                      <div className="tooltip-content tooltip-top-end w-56">
                        <div className="tooltip-arrow"></div>
                        <div className="space-y-1 text-xs">
                          {item.monthly_segments.map((seg, si) => (
                            <div key={si} className="flex justify-between gap-3">
                              <span className="text-gray-300">{seg.month} {seg.year}</span>
                              <span className="tabular-nums text-white">{formatDayCount(seg.unpaid_days)} × ${Number(seg.daily_rate).toFixed(2)} = ${Number(seg.deduction).toFixed(2)}</span>
                            </div>
                          ))}
                          {item.monthly_segments.length > 1 && (
                            <div className="flex justify-between gap-3 border-t border-gray-700 pt-1 mt-1">
                              <span className="text-gray-300 font-medium">Total</span>
                              <span className="tabular-nums text-white font-medium">${item.deduction_amount}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex justify-between">
              <span className="text-gray-600">Leave ({payroll.unpaid_leave_days || 0}d)</span>
              <span className="font-medium text-red-600 tabular-nums">-{formatCurrency(payroll.leave_deduction_amount)}</span>
            </div>
          )
        )}

        {/* Other deductions — adjustment line items, NS, WP, Bond */}
        {(() => {
          const rawBreakdown = payroll.other_deductions_breakdown;
          const adjItems = payroll.adjustment_line_items;
          const METADATA_KEYS = new Set(['adjustment_reason', 'adjustment_reason_display', 'adjustment_notes', 'adjusted_by', 'adjusted_at', 'line_items']);
          const adjDeductions = adjItems?.deductions;
          const deductionLineItems = adjDeductions || rawBreakdown?.line_items?.deductions || [];
          const adjLabels = Array.isArray(adjDeductions)
            ? new Set(adjDeductions.map(i => (i.description || '').toLowerCase().trim()).filter(Boolean))
            : null;
          const otherAmt = parseFloat(payroll.other_deductions) || 0;
          const legacyEntries = rawBreakdown
            ? Object.entries(rawBreakdown).filter(([key, val]) => {
                if (METADATA_KEYS.has(key) || typeof val !== 'number' || val <= 0) return false;
                if (adjLabels !== null) {
                  if (adjLabels.size === 0 && otherAmt <= 0) return false;
                  return !adjLabels.has(key.toLowerCase().trim());
                }
                return true;
              })
            : [];
          const hasItemisedRows = deductionLineItems.length > 0 || legacyEntries.length > 0;
          const nsAmt = parseFloat(payroll.ns_deduction_amount) || 0;
          const wpAmt = parseFloat(payroll.work_permit_replacement_fee) || 0;
          const bondAmt = parseFloat(payroll.sponsorship_bond_deduction) || 0;
          return (
            <>
              {deductionLineItems.map((item, idx) => (
                <div key={`ded-${idx}`} className="flex justify-between">
                  <span className="text-gray-600">{item.description || 'Deduction'}</span>
                  <span className="font-medium text-red-600 tabular-nums">-{formatCurrency(item.amount)}</span>
                </div>
              ))}
              {legacyEntries.map(([label, amount]) => (
                <div key={label} className="flex justify-between">
                  <span className="text-gray-600">{label}</span>
                  <span className="font-medium text-red-600 tabular-nums">-{formatCurrency(amount)}</span>
                </div>
              ))}
              {otherAmt > 0 && !hasItemisedRows && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Other Deductions</span>
                  <span className="font-medium text-red-600 tabular-nums">-{formatCurrency(otherAmt)}</span>
                </div>
              )}
              {nsAmt > 0 && (() => {
                const nsBreakdown = Array.isArray(payroll.ns_deduction_breakdown) ? payroll.ns_deduction_breakdown : [];
                const nsIctDays = parseFloat(payroll.ns_ict_days) || 0;
                const firstEntry = nsBreakdown[0] || {};
                const ictStart = firstEntry.ict_start_date ? new Date(firstEntry.ict_start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : null;
                const ictEnd = firstEntry.ict_end_date ? new Date(firstEntry.ict_end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : null;
                const dateLabel = ictStart && ictEnd ? (ictStart === ictEnd ? ictStart : `${ictStart}–${ictEnd}`) : '';
                const monthlySegments = firstEntry.monthly_breakdown || [];
                return (
                  <div className="flex justify-between items-start">
                    <div className="min-w-0">
                      <span className="text-gray-600 flex items-center gap-1">NS Recovery <InfoBadge color="blue" label="MINDEF" /></span>
                      <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                        {dateLabel && <span className="text-[10px] text-gray-400">{dateLabel}</span>}
                        {nsIctDays > 0 && <InfoBadge color="gray" label={`${nsIctDays}d`} />}
                      </div>
                    </div>
                    <div className="tooltip-container flex-shrink-0 ml-2">
                      <span className="tooltip-trigger font-medium text-red-600 tabular-nums cursor-help">-{formatCurrency(nsAmt)}</span>
                      {monthlySegments.length > 0 && (
                        <div className="tooltip-content tooltip-top-end w-56">
                          <div className="tooltip-arrow"></div>
                          <div className="space-y-1 text-xs">
                            {monthlySegments.map((seg, si) => (
                              <div key={si} className="flex justify-between gap-3">
                                <span className="text-gray-300">{seg.month}</span>
                                <span className="tabular-nums text-white">{seg.ict_days}d × ${Number(seg.daily_rate).toFixed(2)} = ${Number(seg.deduction_amount).toFixed(2)}</span>
                              </div>
                            ))}
                            {monthlySegments.length > 1 && (
                              <div className="flex justify-between gap-3 border-t border-gray-700 pt-1 mt-1">
                                <span className="text-gray-300 font-medium">Total</span>
                                <span className="tabular-nums text-white font-medium">{formatCurrency(nsAmt)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
              {wpAmt > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 flex items-center gap-1">WP Fee <InfoBadge color="orange" label="MOM" /></span>
                  <span className="font-medium text-red-600 tabular-nums">-{formatCurrency(wpAmt)}</span>
                </div>
              )}
              {bondAmt > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Sponsorship Bond</span>
                  <span className="font-medium text-red-600 tabular-nums">-{formatCurrency(bondAmt)}</span>
                </div>
              )}
            </>
          );
        })()}
        <div className="pt-2 mt-2 border-t border-gray-200 flex justify-between font-semibold">
          <span className="text-gray-900">Total Deductions</span>
          <span className="text-red-600 tabular-nums">-{formatCurrency(payroll.total_deductions)}</span>
        </div>
      </div>
    </div>
  );

  // ── Calculation Rates Accordion ────────────────────────────────────────────
  const RatesAccordion = () => {
    const live = payroll.mom_rates_reference;
    const stale = payroll.calculation_metadata?.rates_reference;
    const cur = live?.current_month || stale?.current_month;
    const isWorker = payroll.employee_classification === 'worker';
    const prior = isWorker ? null : (live?.prior_month || stale?.prior_month);
    const fixed = live?.fixed_rates || stale?.fixed_rates;
    const monthlySalary = parseFloat(fixed?.monthly_basic ?? payroll.basic_salary_monthly) || 0;
    const totalAllowances = parseFloat(payroll.total_allowances) || 0;
    const monthlyGross = parseFloat(fixed?.monthly_gross) || (monthlySalary + totalAllowances);
    // FS incomplete-month basic uses monthly ÷ full-month MOM days. Prefer the
    // persisted basic_salary_details snapshot so this panel cannot drift from
    // the Earnings line (live serializer historically put the annual rate here).
    // Use ternary (not `false && value`) before ??: `(false && x)` is false,
    // and ?? only skips null/undefined — so regular runs previously froze at
    // parseFloat(false) => NaN => 0 for MOM Working Days / incomplete rate.
    const curMomDays = parseFloat(
      (isFinalSettlement ? fsBasicDetails?.full_month_working_days : null)
        ?? cur?.mom_working_days
        ?? payroll.working_days_entitled
    ) || 0;
    const incompleteMonthDailyRate = parseFloat(
      (isFinalSettlement ? fsBasicDetails?.proration_daily_rate : null)
        ?? cur?.daily_rate
    ) || (monthlyGross > 0 && curMomDays > 0 ? monthlyGross / curMomDays : 0);
    const curDailyRate = incompleteMonthDailyRate;
    const fsPaidDaysForRates = parseFloat(
      fsBasicDetails?.paid_working_days ?? payroll.working_days_worked
    ) || 0;
    const fsScheduledDaysForRates = parseFloat(
      fsBasicDetails?.scheduled_working_days
        ?? (fsPaidDaysForRates + (parseFloat(fsBasicDetails?.authorised_unpaid_leave_days) || 0))
    ) || 0;
    const fsUnpaidDaysForRates = parseFloat(
      fsBasicDetails?.authorised_unpaid_leave_days ?? payroll.unpaid_leave_days
    ) || 0;
    const curLabel = cur ? `${cur.month?.slice(0, 3)} ${cur.year}` : 'Current';
    const priorLabel = prior ? `${prior.month?.slice(0, 3)} ${prior.year}` : null;
    const WEEK_MAP = { '6_day': 6, '5_half_day': 5.5, '5_5_day': 5.5, '5_day': 5 };
    const daysPerWeek = parseFloat(fixed?.days_per_week) || WEEK_MAP[live?.work_week_type] || WEEK_MAP[cur?.work_week_type] || WEEK_MAP[payroll.employee_work_week_type] || 5;
    const grossDailyRate = parseFloat(fixed?.gross_daily_rate) || (monthlyGross > 0 ? (12 * monthlyGross) / (52 * daysPerWeek) : 0);
    const basicDailyRate = parseFloat(fixed?.basic_daily_rate) || (monthlySalary > 0 ? (12 * monthlySalary) / (52 * daysPerWeek) : 0);
    const normalHoursPerDay = parseFloat(fixed?.normal_hours_per_day) || 8;
    const basicHourly = parseFloat(fixed?.basic_hourly_rate) || (basicDailyRate > 0 && normalHoursPerDay > 0 ? basicDailyRate / normalHoursPerDay : 0);
    const otMultiplier = parseFloat(stale?.fixed_rates?.ot_multiplier ?? payroll.overtime_rate_multiplier) || 1.5;
    const hourlyOtRate = parseFloat(fixed?.hourly_ot_rate) || (monthlySalary > 0 ? (12 * monthlySalary * 1.5) / (44 * 52) : 0);
    const restDayOtDailyRate = parseFloat(fixed?.rest_day_ot_daily_rate) || (monthlySalary > 0 ? (12 * monthlySalary * 1.5) / (52 * daysPerWeek) : 0);
    const restDayOtHourlyRate = parseFloat(fixed?.rest_day_ot_hourly_rate) || (restDayOtDailyRate > 0 && normalHoursPerDay > 0 ? restDayOtDailyRate / normalHoursPerDay : 0);
    // MOM s.37 employer-request rest-day tiers apply from the May-2026 cutover
    // onward (period-start gated by the backend). Pre-cutover periods keep the
    // legacy flat 1.5x display. Default to legacy when the flag is absent (old rows).
    const restDayPayModel = fixed?.rest_day_pay_model || stale?.fixed_rates?.rest_day_pay_model;
    const restDayTiered = restDayPayModel === 'mom_tiered';
    const restDayHalfDayRate = parseFloat(fixed?.rest_day_half_day_rate) || basicDailyRate;
    const restDayFullDayRate = parseFloat(fixed?.rest_day_full_day_rate) || (basicDailyRate * 2);
    // Exact tier-threshold hours from the worker's WorkScheduleTemplate (the
    // value the pay engine actually uses), so the displayed thresholds are
    // transparent. Falls back to the generic normal-hours figure if absent.
    const restDayNormalHours = parseFloat(fixed?.rest_day_normal_hours) || normalHoursPerDay;
    const restDayHalfHours = parseFloat(fixed?.rest_day_half_day_hours) || (restDayNormalHours / 2);
    const fmtHrs = (h) => `${parseFloat(Number(h).toFixed(2))}`;

    return (
      <AccordionSection
        expanded={accordions.rates}
        onToggle={() => setAccordions(prev => ({ ...prev, rates: !prev.rates }))}
        title="Calculation Rates"
        icon={<Calculator className="w-4 h-4 text-gray-600" />}
        iconBg="bg-gray-100"
        theme="admin"
        contentVariant="padded"
      >
        <div className="text-xs space-y-3">
          {/* HR decision guide: two MOM daily rates look similar — pick by pay item */}
          {(curDailyRate > 0 || basicDailyRate > 0) && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2.5">
              <div className="flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <CircleHelp className="w-3.5 h-3.5 text-red-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-900">Which daily rate?</p>
                  <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                    MOM has two daily figures. Use the one that matches the pay item you are checking — they are not interchangeable.
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                {curDailyRate > 0 && (
                  <div className="flex items-start justify-between gap-3 rounded-lg bg-white border border-gray-100 px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-900">Incomplete-month rate</p>
                      <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                        Unpaid leave · absence · mid-month join/leave
                      </p>
                    </div>
                    <div className="tooltip-container inline-flex flex-shrink-0">
                      <span className="tooltip-trigger text-xs font-semibold text-gray-900 tabular-nums whitespace-nowrap cursor-help">
                        {formatCurrency(curDailyRate)}/day
                      </span>
                      <div className="tooltip-content tooltip-top-end w-56">
                        <div className="tooltip-arrow"></div>
                        <div className="text-xs space-y-1">
                          <div className="tabular-nums text-white">
                            {formatCurrency(monthlyGross)} ÷ {curMomDays} days = {formatCurrency(curDailyRate)}/day
                          </div>
                          <div className="text-gray-400">Canonical incomplete-month figure for this panel</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {basicDailyRate > 0 && (
                  <div className="flex items-start justify-between gap-3 rounded-lg bg-white border border-gray-100 px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-900">
                        Annual daily rate
                        {Math.abs(grossDailyRate - basicDailyRate) > 0.01 ? ' (basic)' : ''}
                      </p>
                      <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                        OT · rest day · PH work · AL encashment
                      </p>
                    </div>
                    <div className="tooltip-container inline-flex flex-shrink-0">
                      <span className="tooltip-trigger text-xs font-semibold text-gray-900 tabular-nums whitespace-nowrap cursor-help">
                        {formatCurrency(basicDailyRate)}/day
                      </span>
                      <div className="tooltip-content tooltip-top-end w-56">
                        <div className="tooltip-arrow"></div>
                        <div className="text-xs space-y-1">
                          <div className="tabular-nums text-white">
                            (12 × {formatCurrency(monthlySalary)}) ÷ (52 × {daysPerWeek}d)
                          </div>
                          <div className="text-gray-400">Do not use for unpaid leave docking</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-between items-center">
            <span className="text-gray-500">Monthly Basic</span>
            <span className="text-gray-900 tabular-nums font-medium">{formatCurrency(monthlySalary)}</span>
          </div>
          {monthlyGross > 0 && Math.abs(monthlyGross - monthlySalary) > 0.01 && (
            <div className="flex justify-between items-center">
              <span className="text-gray-500">Monthly Gross <span className="text-gray-400 font-normal">(incl. allowances)</span></span>
              <span className="text-gray-900 tabular-nums font-medium">{formatCurrency(monthlyGross)}</span>
            </div>
          )}
          <div className="pt-2 mt-1 border-t border-gray-100">
            <h6 className="text-xs font-medium text-gray-400 uppercase mb-0.5">
              {isFinalSettlement ? 'Incomplete Month Basic' : 'Unpaid Leave / Incomplete Month'}
            </h6>
            <p className="text-[11px] text-gray-400 mb-2">
              Monthly ÷ MOM working days this month — dock leave &amp; absence here
            </p>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-xs text-gray-400 uppercase">
                  <th className="text-left font-medium pb-1"></th>
                  <th className="text-right font-medium pb-1 w-[72px]">{curLabel}</th>
                  {prior && <th className="text-right font-medium pb-1 w-[72px]">{priorLabel}</th>}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-gray-600 py-1">MOM Working Days</td>
                  <td className="text-right text-gray-900 tabular-nums font-medium py-1">{curMomDays}</td>
                  {prior && <td className="text-right text-gray-900 tabular-nums font-medium py-1">{prior.mom_working_days}</td>}
                </tr>
                {/* Incomplete-month $/day lives only in "Which daily rate?" above.
                    Keep a prior-month column here when staff tail-period applies. */}
                {prior && (
                  <tr>
                    <td className="text-gray-600 py-1">
                      Prior-month rate
                      <span className="block text-[10px] text-gray-400 font-normal normal-case">
                        Tail-period docking ({priorLabel})
                      </span>
                    </td>
                    <td className="text-right text-gray-400 tabular-nums py-1">—</td>
                    <td className="text-right py-1">
                      <div className="tooltip-container inline-flex">
                        <span className="tooltip-trigger text-gray-900 tabular-nums font-medium cursor-help">
                          {formatCurrency(prior.daily_rate)}/day
                        </span>
                        <div className="tooltip-content tooltip-top-end w-52">
                          <div className="tooltip-arrow"></div>
                          <div className="text-xs tabular-nums text-white">
                            {formatCurrency(monthlyGross)} ÷ {prior.mom_working_days} days
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                {isFinalSettlement && fsPaidDaysForRates > 0 && (
                  <>
                    <tr>
                      <td className="text-gray-600 py-1">Scheduled days</td>
                      <td className="text-right text-gray-900 tabular-nums font-medium py-1">{formatCompactNumber(fsScheduledDaysForRates)}</td>
                      {prior && <td />}
                    </tr>
                    {fsUnpaidDaysForRates > 0 && (
                      <tr>
                        <td className="text-gray-600 py-1">Unpaid leave (included)</td>
                        <td className="text-right text-gray-900 tabular-nums font-medium py-1">{formatCompactNumber(fsUnpaidDaysForRates)}</td>
                        {prior && <td />}
                      </tr>
                    )}
                    <tr>
                      <td className="text-gray-600 py-1">Paid days</td>
                      <td className="text-right text-gray-900 tabular-nums font-medium py-1">{formatCompactNumber(fsPaidDaysForRates)}</td>
                      {prior && <td />}
                    </tr>
                    <tr>
                      <td className="text-gray-600 py-1 pt-1.5">Basic salary check</td>
                      <td className="text-right text-gray-900 tabular-nums font-medium py-1 pt-1.5" colSpan={prior ? 2 : 1}>
                        <div className="tooltip-container inline-flex">
                          <span className="tooltip-trigger cursor-help">
                            {formatCurrency(curDailyRate)}/day × {formatCompactNumber(fsPaidDaysForRates)} = {formatCurrency(fsBasicEarned)}
                          </span>
                          <div className="tooltip-content tooltip-top-end w-64">
                            <div className="tooltip-arrow"></div>
                            <div className="text-xs text-gray-400">
                              Must match Earnings → Basic Salary. Annual rates below are for OT / rest day only.
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
          <div className="pt-2 mt-1 border-t border-gray-100 space-y-2">
            <h6 className="text-xs font-medium text-gray-400 uppercase mb-0.5">Annual Formula Rates</h6>
            <p className="text-[11px] text-gray-400 -mt-0.5 mb-1">
              (12 × monthly) ÷ (52 × days/wk) — OT, rest day, PH &amp; encashment only
            </p>
            {/* Annual basic daily $/day lives only in "Which daily rate?" above.
                Show gross separately when it differs (encashment / paid PH basis). */}
            {Math.abs(grossDailyRate - basicDailyRate) > 0.01 && grossDailyRate > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Gross Daily Rate</span>
                <div className="tooltip-container inline-flex">
                  <span className="tooltip-trigger text-gray-900 tabular-nums font-medium cursor-help">{formatCurrency(grossDailyRate)}/day</span>
                  <div className="tooltip-content tooltip-top-end w-56">
                    <div className="tooltip-arrow"></div>
                    <div className="text-xs space-y-1">
                      <div className="tabular-nums text-white">(12 × {formatCurrency(monthlyGross)}) ÷ (52 × {daysPerWeek}d)</div>
                      <div className="text-gray-400">AL encashment, paid PH, salary in lieu</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {basicHourly > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Basic Hourly Rate</span>
                <div className="tooltip-container inline-flex">
                  <span className="tooltip-trigger text-gray-900 tabular-nums font-medium cursor-help">{formatCurrency(basicHourly)}/hr</span>
                  <div className="tooltip-content tooltip-top-end w-56">
                    <div className="tooltip-arrow"></div>
                    <div className="text-xs space-y-1">
                      <div className="tabular-nums text-white">{formatCurrency(basicDailyRate)}/day ÷ {normalHoursPerDay}h/day</div>
                      <div className="text-gray-400">Basic daily rate ÷ hours per day</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {hourlyOtRate > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Hourly OT Rate</span>
                <div className="tooltip-container inline-flex">
                  <span className="tooltip-trigger text-gray-900 tabular-nums font-medium cursor-help">{formatCurrency(hourlyOtRate)}/hr</span>
                  <div className="tooltip-content tooltip-top-end w-56">
                    <div className="tooltip-arrow"></div>
                    <div className="text-xs space-y-1">
                      <div className="tabular-nums text-white">(12 × {formatCurrency(monthlySalary)} × {otMultiplier}) ÷ (44 × 52)</div>
                      <div className="text-gray-400">MOM: (Basic × 12 × 1.5) ÷ (44 × 52)</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {restDayTiered ? (
              <>
                {restDayHalfDayRate > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Rest Day <span className="text-gray-400">(≤ {fmtHrs(restDayHalfHours)}h)</span></span>
                    <div className="tooltip-container inline-flex">
                      <span className="tooltip-trigger text-gray-900 tabular-nums font-medium cursor-help">
                        {formatCurrency(restDayHalfDayRate)}/day
                      </span>
                      <div className="tooltip-content tooltip-top-end w-64">
                        <div className="tooltip-arrow"></div>
                        <div className="text-xs space-y-1">
                          <div className="tabular-nums text-white">1 × basic daily ({formatCurrency(basicDailyRate)}/day)</div>
                          <div className="text-gray-400">
                            MOM s.37 (employer request): worked ≤ {fmtHrs(restDayHalfHours)}h
                            (½ of {fmtHrs(restDayNormalHours)}h normal day) → 1 day&apos;s pay
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {restDayFullDayRate > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">
                      Rest Day <span className="text-gray-400">(&gt; {fmtHrs(restDayHalfHours)}h, ≤ {fmtHrs(restDayNormalHours)}h)</span>
                    </span>
                    <div className="tooltip-container inline-flex">
                      <span className="tooltip-trigger text-gray-900 tabular-nums font-medium cursor-help">
                        {formatCurrency(restDayFullDayRate)}/day
                      </span>
                      <div className="tooltip-content tooltip-top-end w-64">
                        <div className="tooltip-arrow"></div>
                        <div className="text-xs space-y-1">
                          <div className="tabular-nums text-white">2 × basic daily ({formatCurrency(basicDailyRate)}/day)</div>
                          <div className="text-gray-400">
                            MOM s.37 (employer request): worked &gt; {fmtHrs(restDayHalfHours)}h,
                            up to {fmtHrs(restDayNormalHours)}h → 2 days&apos; pay
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {hourlyOtRate > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">
                      Rest Day OT <span className="text-gray-400">(beyond {fmtHrs(restDayNormalHours)}h)</span>
                    </span>
                    <div className="tooltip-container inline-flex">
                      <span className="tooltip-trigger text-gray-900 tabular-nums font-medium cursor-help">{formatCurrency(hourlyOtRate)}/hr</span>
                      <div className="tooltip-content tooltip-top-end w-64">
                        <div className="tooltip-arrow"></div>
                        <div className="text-xs space-y-1">
                          <div className="tabular-nums text-white">(12 × {formatCurrency(monthlySalary)} × {otMultiplier}) ÷ (44 × 52)</div>
                          <div className="text-gray-400">Hours beyond the normal day on a rest day → 1.5× hourly OT</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {restDayOtDailyRate > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Rest Day OT Rate</span>
                    <div className="tooltip-container inline-flex">
                      <span className="tooltip-trigger text-gray-900 tabular-nums font-medium cursor-help">{formatCurrency(restDayOtDailyRate)}/day</span>
                      <div className="tooltip-content tooltip-top-end w-56">
                        <div className="tooltip-arrow"></div>
                        <div className="text-xs space-y-1">
                          <div className="tabular-nums text-white">(12 × {formatCurrency(monthlySalary)} × {otMultiplier}) ÷ (52 × {daysPerWeek}d)</div>
                          <div className="text-gray-400">MOM: (Basic × 12 × 1.5) ÷ (52 × days/wk)</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {restDayOtHourlyRate > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Rest Day Hourly OT Rate</span>
                    <div className="tooltip-container inline-flex">
                      <span className="tooltip-trigger text-gray-900 tabular-nums font-medium cursor-help">{formatCurrency(restDayOtHourlyRate)}/hr</span>
                      <div className="tooltip-content tooltip-top-end w-56">
                        <div className="tooltip-arrow"></div>
                        <div className="text-xs space-y-1">
                          <div className="tabular-nums text-white">{formatCurrency(restDayOtDailyRate)}/day ÷ {normalHoursPerDay}h/day</div>
                          <div className="text-gray-400">Rest Day OT Rate ÷ hours per day</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </AccordionSection>
    );
  };

  // ── CPF & Employer Costs Accordion ─────────────────────────────────────────
  const CpfEmployerAccordion = () => {
    if (!isCpfApplicable && !(parseFloat(payroll.sdl_amount) > 0) && !(parseFloat(payroll.foreign_worker_levy) > 0)) return null;
    return (
      <AccordionSection
        expanded={accordions.deductions}
        onToggle={() => setAccordions(prev => ({ ...prev, deductions: !prev.deductions }))}
        title={isCpfApplicable ? 'CPF & Employer Costs' : 'Employer Costs'}
        icon={<Shield className="w-4 h-4 text-blue-600" />}
        iconBg="bg-blue-100"
        theme="admin"
        contentVariant="padded"
        overflowVisible
      >
        {isCpfApplicable && (
          <div className="space-y-1 text-xs">
            <h6 className="text-xs font-medium text-gray-500 uppercase mb-1.5">CPF Contribution Breakdown</h6>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 flex items-center gap-1">Employee Share {payroll.cpf_employee_rate_display !== 'N/A' && <InfoBadge color="blue" label={payroll.cpf_employee_rate_display} />}</span>
              <span className="text-gray-900 tabular-nums">{formatCurrency(payroll.cpf_employee_contribution)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 flex items-center gap-1">Employer Share {payroll.cpf_employer_rate_display !== 'N/A' && <InfoBadge color="red" label={payroll.cpf_employer_rate_display} />}</span>
              <span className="text-gray-900 tabular-nums">{formatCurrency(payroll.cpf_employer_contribution)}</span>
            </div>
            <div className="flex justify-between font-medium pt-1 border-t border-gray-100">
              <span className="text-gray-700">Total CPF</span>
              <span className="text-gray-900 tabular-nums">{formatCurrency((parseFloat(payroll.cpf_employee_contribution) || 0) + (parseFloat(payroll.cpf_employer_contribution) || 0))}</span>
            </div>
          </div>
        )}
        {isCpfApplicable && payroll.shg_contributions_display && !payroll.shg_contributions_display.opt_out && parseFloat(payroll.shg_contributions_display.total) > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200 space-y-1 text-xs">
            <h6 className="text-xs font-medium text-gray-500 uppercase mb-1.5">SHG Contributions</h6>
            {parseFloat(payroll.shg_contributions_display.cdac) > 0 && <div className="flex justify-between"><span className="text-gray-600">CDAC</span><span className="text-gray-900 tabular-nums">{formatCurrency(payroll.shg_contributions_display.cdac)}</span></div>}
            {parseFloat(payroll.shg_contributions_display.sinda) > 0 && <div className="flex justify-between"><span className="text-gray-600">SINDA</span><span className="text-gray-900 tabular-nums">{formatCurrency(payroll.shg_contributions_display.sinda)}</span></div>}
            {parseFloat(payroll.shg_contributions_display.mbmf) > 0 && <div className="flex justify-between"><span className="text-gray-600">MBMF</span><span className="text-gray-900 tabular-nums">{formatCurrency(payroll.shg_contributions_display.mbmf)}</span></div>}
            {parseFloat(payroll.shg_contributions_display.ecf) > 0 && <div className="flex justify-between"><span className="text-gray-600">ECF</span><span className="text-gray-900 tabular-nums">{formatCurrency(payroll.shg_contributions_display.ecf)}</span></div>}
          </div>
        )}
        {(parseFloat(payroll.sdl_amount) > 0 || parseFloat(payroll.foreign_worker_levy) > 0 || (isCpfApplicable && parseFloat(payroll.cpf_employer_contribution) > 0)) && (
          <div className={`space-y-1 text-xs ${isCpfApplicable ? 'mt-3 pt-3 border-t border-gray-200' : ''}`}>
            <h6 className="text-xs font-medium text-gray-500 uppercase mb-1.5">Employer Costs</h6>
            {isCpfApplicable && parseFloat(payroll.cpf_employer_contribution) > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600 flex items-center gap-1">CPF Employer {payroll.cpf_employer_rate_display !== 'N/A' && <InfoBadge color="red" label={payroll.cpf_employer_rate_display} />}</span>
                <span className="text-gray-700 tabular-nums">{formatCurrency(payroll.cpf_employer_contribution)}</span>
              </div>
            )}
            {parseFloat(payroll.sdl_amount) > 0 && (() => {
              const sdlEligible = parseFloat(payroll.sdl_eligible_wages) || 0;
              const sdlRate = parseFloat(payroll.sdl_rate) || 0;
              const sdlRatePct = (sdlRate * 100).toFixed(2);
              const sdlAmt = parseFloat(payroll.sdl_amount) || 0;
              return (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">SDL</span>
                  <div className="tooltip-container">
                    <span className="tooltip-trigger text-gray-700 tabular-nums cursor-help">{formatCurrency(sdlAmt)}</span>
                    <div className="tooltip-content tooltip-top-end w-64">
                      <div className="tooltip-arrow"></div>
                      <div className="text-xs space-y-1">
                        <div className="flex justify-between gap-3">
                          <span className="text-gray-400">SDL-eligible wages</span>
                          <span className="tabular-nums text-white">{formatCurrency(sdlEligible)}</span>
                        </div>
                        <div className="flex justify-between gap-3">
                          <span className="text-gray-400">Rate</span>
                          <span className="tabular-nums text-white">{sdlRatePct}%</span>
                        </div>
                        <div className="flex justify-between gap-3 border-t border-gray-600 pt-1 mt-1">
                          <span className="text-gray-400">{formatCurrency(sdlEligible)} × {sdlRatePct}%</span>
                          <span className="tabular-nums text-white">{formatCurrency(sdlAmt)}</span>
                        </div>
                        <div className="text-[10px] text-gray-500 mt-0.5">Rounded down per MOM compliance</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
            {parseFloat(payroll.foreign_worker_levy) > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600 flex items-center gap-1">
                  FWL
                  <InfoBadge color="orange" label={fwlSourceLabel} />
                </span>
                <div className="tooltip-container">
                  <span className="tooltip-trigger text-gray-700 tabular-nums cursor-help">
                    {formatCurrency(payroll.foreign_worker_levy)}
                  </span>
                  <div className="tooltip-content tooltip-top-end w-72">
                    <div className="tooltip-arrow"></div>
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-400">Payroll month</span>
                        <span className="text-white">{fwlPayrollPeriod || '—'}</span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-400">MOM bill reference</span>
                        <span className="text-white">{fwlBillPeriod || 'Not available'}</span>
                      </div>
                      {fwlBreakdown.monthly_levy_rate != null && (
                        <div className="flex justify-between gap-3">
                          <span className="text-gray-400">Monthly levy rate</span>
                          <span className="text-white tabular-nums">
                            {formatCurrency(fwlBreakdown.monthly_levy_rate)}
                          </span>
                        </div>
                      )}
                      {fwlBreakdown.levy_source === 'estimate' && fwlBreakdown.daily_levy_rate != null && (
                        <div className="flex justify-between gap-3">
                          <span className="text-gray-400">MOM daily rate</span>
                          <span className="text-white tabular-nums">
                            {formatCurrency(fwlBreakdown.daily_levy_rate)}
                          </span>
                        </div>
                      )}
                      {fwlBreakdown.levy_source === 'estimate' && fwlBreakdown.period_days != null && (
                        <div className="flex justify-between gap-3">
                          <span className="text-gray-400">Billable days</span>
                          <span className="text-white tabular-nums">
                            {fwlBreakdown.period_days}
                            {fwlBreakdown.is_full_calendar_month ? ' (full month)' : ''}
                          </span>
                        </div>
                      )}
                      {fwlBreakdown.levy_source === 'estimate' && (
                        <div className="flex justify-between gap-3 border-t border-gray-600 pt-1 mt-1">
                          <span className="text-gray-400">
                            {fwlBreakdown.is_full_calendar_month
                              ? 'Full month (≤ monthly rate)'
                              : `${formatCurrency(fwlBreakdown.daily_levy_rate)} × ${fwlBreakdown.period_days}`}
                          </span>
                          <span className="tabular-nums text-white">
                            {formatCurrency(payroll.foreign_worker_levy)}
                          </span>
                        </div>
                      )}
                      <div className="border-t border-gray-600 pt-1 mt-1 text-[10px] text-gray-500">
                        {fwlBreakdown.note || 'Estimated from the configured MOM levy rate.'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </AccordionSection>
    );
  };

  // ── YTD Accordion ──────────────────────────────────────────────────────────
  const YtdAccordion = () => (
    <AccordionSection
      expanded={accordions.ytd}
      onToggle={() => setAccordions(prev => ({ ...prev, ytd: !prev.ytd }))}
      title={<>Year-to-Date Summary <InfoBadge color="purple" label={String(payroll.ytd_summary?.year || new Date().getFullYear())} className="ml-2" /></>}
      icon={<Calendar className="w-4 h-4 text-purple-600" />}
      iconBg="bg-purple-100"
      theme="admin"
      contentVariant="padded"
    >
      {payroll.ytd_summary ? (
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-gray-600">YTD Gross Pay</span><span className="font-medium text-gray-900 tabular-nums">{formatCurrency(payroll.ytd_summary.gross_pay)}</span></div>
          {isCpfApplicable && (
            <>
              <div className="flex justify-between items-center"><span className="text-gray-600 flex items-center gap-1">YTD CPF <InfoBadge color="blue" label="Employee" /></span><span className="font-medium text-gray-900 tabular-nums">{formatCurrency(payroll.ytd_summary.cpf_employee)}</span></div>
              <div className="flex justify-between items-center"><span className="text-gray-600 flex items-center gap-1">YTD CPF <InfoBadge color="red" label="Employer" /></span><span className="font-medium text-gray-900 tabular-nums">{formatCurrency(payroll.ytd_summary.cpf_employer)}</span></div>
            </>
          )}
          <div className="flex justify-between items-center"><span className="text-gray-600 flex items-center gap-1">YTD Tax <InfoBadge color="red" label="IRAS" /></span><span className="font-medium text-gray-900 tabular-nums">{formatCurrency(payroll.ytd_summary.tax_deduction)}</span></div>
          <div className="pt-2 mt-2 border-t border-gray-200 flex justify-between font-semibold"><span className="text-gray-900">YTD Net Pay</span><span className="text-gray-900 tabular-nums">{formatCurrency(payroll.ytd_summary.net_pay)}</span></div>
        </div>
      ) : (
        <p className="text-sm text-gray-500 italic">YTD data not available</p>
      )}
    </AccordionSection>
  );

  // ── AW Ceiling Accordion ───────────────────────────────────────────────────
  const AwCeilingAccordion = () => {
    if (!isCpfApplicable) return null;
    return (
      <AccordionSection
        expanded={accordions.awCeiling}
        onToggle={() => setAccordions(prev => ({ ...prev, awCeiling: !prev.awCeiling }))}
        title={<>CPF AW Ceiling <InfoBadge color="amber" label={String(payroll.aw_ceiling_info?.year || new Date().getFullYear())} className="ml-2" /></>}
        icon={<AlertTriangle className="w-4 h-4 text-amber-600" />}
        iconBg="bg-amber-100"
        theme="admin"
        contentVariant="padded"
      >
        {payroll.aw_ceiling_info ? (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-600">Annual AW Ceiling</span><span className="font-medium text-gray-900 tabular-nums">{formatCurrency(payroll.aw_ceiling_info.aw_annual_ceiling)}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">YTD OW</span><span className="font-medium text-gray-900 tabular-nums">{formatCurrency(payroll.aw_ceiling_info.ytd_ow_cumulative)}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Monthly OW Ceiling</span><span className="font-medium text-gray-900 tabular-nums">{formatCurrency(payroll.aw_ceiling_info.ow_monthly_ceiling)}</span></div>
            <div className="pt-2 mt-2 border-t border-gray-200 flex justify-between font-semibold">
              <span className="text-gray-900">Remaining AW Ceiling</span>
              <span className={`tabular-nums ${payroll.aw_ceiling_info.is_ceiling_reached ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(payroll.aw_ceiling_info.remaining_aw_ceiling)}</span>
            </div>
            {payroll.aw_ceiling_info.is_ceiling_reached && (
              <p className="text-xs text-red-600 mt-2 bg-red-50 p-2 rounded-xl">AW ceiling reached. Additional bonuses will not attract CPF.</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">AW ceiling data not available</p>
        )}
      </AccordionSection>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <SummaryCards />

      <div className="grid grid-cols-2 gap-4">
        <EarningsColumn />
        <DeductionsColumn />
      </div>

      <div className="space-y-3 mt-4">
        <RatesAccordion />
        <CpfEmployerAccordion />
        <YtdAccordion />
        <AwCeilingAccordion />
      </div>

      {payroll.adjustment_notes && (
        <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
          <h6 className="text-xs font-medium text-amber-800 uppercase mb-1">Adjustment Notes</h6>
          <p className="text-sm text-amber-700">{payroll.adjustment_notes}</p>
        </div>
      )}
    </div>
  );
};

export default EmployeePayrollBreakdown;
